import React, {
	useEffect,
	useContext,
	useState,
	useCallback,
	useRef,
} from 'react';
import { useHistory } from 'react-router-dom';

// ANTD
import { Col, Row } from 'antd';

// ICONS
import { FaLock } from 'react-icons/fa';

// REDUX
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import {
	limpaLogin,
	logInRequest,
	logOffRequest,
} from 'store/modules/api/sgu/loginUnico/actions';
import { limpaMainframe } from 'store/modules/sgu/actions';
import {
	addNotifications,
	clearNotifications,
} from 'store/modules/app/notifications/actions';
import {
	alteraSenhaUsuarioClear,
	alteraSenhaUsuarioRequest,
} from 'store/modules/api/sguService/alteraSenhaUsuario/actions';
import { AlteraSenhaUsuarioRequest } from 'store/modules/api/sguService/alteraSenhaUsuario/types';
import { LoginRequest } from 'store/modules/api/sgu/loginUnico/types';
import { setLoader } from 'store/modules/app/loader/actions';

// UTILS
import handleFailed from 'utils/getValidations';

// COMPONENTS
import { AuthContext, IAuthContext } from 'react-oauth2-code-pkce';

// FORM
import { Field, Form, Formik } from 'formik';

// COMPONENTS COMMON
import Alert from 'components/Common/Notifications/Alert';
import InputMask from 'components/Common/Form/Input/InputMask';
import Input from 'components/Common/Form/Input/Input';
import ButtonImage from 'components/Common/ButtonImage';
import Error from 'components/Common/Notifications/FormError/index';
import {
	Notification,
	Container,
	Header,
	ButtonGOV,
	ButtonAREA,
	ContainerGeral,
	LogoBU,
	Text,
} from './styled';
import { FormLogin, initialValues } from './form';

interface LoginProps {
	onSelectAuthFlow?: (type: 'CYBERARK' | 'DIRECT') => void;
}

const Login: React.FC<LoginProps> = ({ onSelectAuthFlow }) => {
	const dispatch = useDispatch();
	const history = useHistory();

	const testMode =
		process.env.REACT_APP_ENVIRONMENT !== undefined &&
		process.env.REACT_APP_ENVIRONMENT === 'TEST';

	let authContext: Partial<IAuthContext> = {};
	try {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		authContext = useContext(AuthContext);
	} catch (e) {
		// Context missing - Expected when in Selection Mode
	}

	const { token, tokenData, login, logOut, loginInProgress, error } =
		authContext;

	const autoLoginAttempted = useRef(false);

	useEffect(() => {
		if (login && !onSelectAuthFlow) {
			if (!token && !loginInProgress && !error && !testMode) {
				const hasCyberArkToken = sessionStorage.getItem('cyberark_token_saved');
				const isLogoutFlow = sessionStorage.getItem('is_logout_flow');

				if (hasCyberArkToken && !isLogoutFlow && !autoLoginAttempted.current) {
					console.log(
						'Fluxo contínuo detectado: Iniciando IDPSP automaticamente...',
					);
					autoLoginAttempted.current = true;
					login();
				}
			}
		}
	}, [token, loginInProgress, error, login, testMode, onSelectAuthFlow]);

	const [formInitialValues, setFormInitialValues] =
		useState<FormLogin>(initialValues);

	const { alteraSenhaUsuario } = useSelector(
		(state: ApplicationState) => state.api.sguService,
	);

	const { user, reload, resetSenha } = useSelector(
		(state: ApplicationState) => state.api.sgu.loginUnico,
	);

	const { notifications } = useSelector((state: ApplicationState) => state.app);

	const { consultaMainframe } = useSelector(
		(state: ApplicationState) => state.sgu,
	);

	const { errors } = useSelector(
		(state: ApplicationState) => state.app.notifications,
	);

	useEffect(() => {
		if (testMode) {
			if (!resetSenha?.status) {
				setFormInitialValues(initialValues);
			}
			if (resetSenha?.status) {
				setFormInitialValues((prev: FormLogin) => ({
					...prev,
					alteraSenha: true,
				}));
			}
		}
	}, [testMode, resetSenha]);

	useEffect(() => {
		if (!testMode && !onSelectAuthFlow) {
			setLoader(loginInProgress || false);
		}
	}, [testMode, loginInProgress, onSelectAuthFlow]);

	useEffect(() => {
		if (testMode && alteraSenhaUsuario.status === 200) {
			dispatch(limpaLogin());
		}
	}, [testMode, dispatch, alteraSenhaUsuario.status]);

	useEffect(() => {
		if (!testMode && !onSelectAuthFlow) {
			const timer = setTimeout(() => {
				if (
					errors &&
					errors[0] === 'Usuário não cadastrado ou inativo no sistema'
				) {
					// eslint-disable-next-line no-alert
					alert('Usuário não cadastrado ou inativo no sistema');
					dispatch(clearNotifications());
					if (tokenData && logOut) {
						logOut();
					}
				}
			}, 2000);
			return () => clearTimeout(timer);
		}
	}, [testMode, logOut, errors, dispatch, tokenData, onSelectAuthFlow]);

	useEffect(() => {
		if (!testMode && token) {
			try {
				if (tokenData?.preferred_username) {
					sessionStorage.removeItem('is_logout_flow');

					dispatch(
						logInRequest(token, {
							cpf: tokenData?.preferred_username,
						}),
					);
				}
			} catch (err) {
				handleFailed(err);
			}
		}
	}, [testMode, tokenData, token, dispatch]);

	useEffect(() => {
		dispatch(limpaMainframe());
		setLoader(false);
		dispatch(limpaLogin());
		if (testMode) {
			dispatch(alteraSenhaUsuarioClear());
		}
	}, [testMode, dispatch]);

	useEffect(() => {
		if (consultaMainframe.status !== 0) dispatch(limpaMainframe());
	}, [consultaMainframe, dispatch]);

	useEffect(() => {
		if (reload) {
			sessionStorage.setItem('is_logout_flow', 'true');
			localStorage.clear();

			if (testMode) {
				dispatch(logOffRequest());
				window.location.reload();
			} else {
				if (tokenData && logOut) {
					logOut();
				}
				dispatch(logOffRequest());
				sessionStorage.removeItem('active_flow');
				sessionStorage.removeItem('cyberark_token_saved');
				history.push('/');
				window.location.reload();
			}
		}
	}, [testMode, dispatch, reload, logOut, history, tokenData]);

	useEffect(() => {
		if (!user?.login) {
			addNotifications({
				errors: ['O usuário não possui acesso a nenhum orgão do poupatempo.'],
			});
			dispatch(logOffRequest());
		}
	}, [dispatch, user]);

	const handleModeTestSubmit = useCallback(
		(formValues: FormLogin) => {
			if (alteraSenhaUsuario?.status === 200) {
				dispatch(alteraSenhaUsuarioClear());
			}
			setFormInitialValues(formValues);
			if (resetSenha?.status && formValues.senhaNova) {
				const payload: AlteraSenhaUsuarioRequest = {
					cpf: (formValues.cpf = formValues.cpf.replace(/[.-]+/g, '')),
					senhaAtual: formValues.senha,
					senhaNova: formValues.senhaNova,
				};
				dispatch(alteraSenhaUsuarioRequest(payload));
			} else if (token) {
				const payload: LoginRequest = {
					cpf: (formValues.cpf = formValues.cpf.replace(/[.-]+/g, '')),
				};
				dispatch(logInRequest(token, payload));
			}
		},
		[alteraSenhaUsuario, dispatch, token, resetSenha],
	);

	const handleMinhaAreaClick = () => {
		sessionStorage.removeItem('is_logout_flow');
		if (onSelectAuthFlow) {
			onSelectAuthFlow('CYBERARK');
		} else if (login) {
			login();
		}
	};

	const handleGovBrClick = () => {
		sessionStorage.removeItem('is_logout_flow');
		if (onSelectAuthFlow) {
			onSelectAuthFlow('DIRECT');
		}
	};

	return testMode ? (
		<Notification>
			{resetSenha.status && (
				<Alert message={resetSenha.message} type="info" onClose={() => {}} />
			)}
			{alteraSenhaUsuario.status === 200 && (
				<Alert
					message="Senha alterada com sucesso. Faça login com a nova senha"
					type="info"
					onClose={() => {}}
				/>
			)}
			<Container>
				<Header>
					<FaLock />
					<h1>LOGIN</h1>
				</Header>
				<Formik
					initialValues={formInitialValues}
					onSubmit={handleModeTestSubmit}
				>
					{formik => (
						<Form autoComplete="off">
							<Row gutter={[0, 10]}>
								<Col span={24}>
									<Field
										as={InputMask}
										title="Usuário"
										name="cpf"
										maskChar=""
										size={80}
										mask="999.999.999-99"
										error={formik.errors.validacaoLogin || formik.errors.cpf}
									/>
								</Col>
							</Row>
							{!resetSenha?.status && (
								<Row gutter={[0, 10]}>
									<Col>
										<Field
											id="senha-login"
											title="Senha"
											name="senha"
											maxLength={10}
											size={80}
											as={Input}
											type="password"
											error={
												formik.errors.validacaoLogin || formik.errors.senha
											}
										/>
									</Col>
								</Row>
							)}
							{resetSenha?.status && (
								<>
									<Row gutter={[0, 10]}>
										<Col>
											<Field
												id="senhaNova"
												title="Nova Senha"
												name="senhaNova"
												maxLength={10}
												size={80}
												as={Input}
												type="password"
												defaultValue=""
												autoComplete="off"
												error={formik.errors.validaNovaSenha}
											/>
										</Col>
									</Row>
									<Row gutter={[0, 10]}>
										<Col>
											<Field
												title="Confirmação"
												name="confirmacaoSenha"
												maxLength={10}
												size={80}
												as={Input}
												autoComplete="off"
												type="password"
												error={formik.errors.validaNovaSenha}
											/>
										</Col>
									</Row>
								</>
							)}
							<Row gutter={[0, 10]}>
								<Col />
							</Row>
							<Row gutter={[20, 10]} justify="center">
								{resetSenha?.status && (
									<Col>
										<ButtonImage
											type="button"
											src="retornar"
											onClick={() => dispatch(limpaLogin())}
										/>
									</Col>
								)}
								<Col>
									<ButtonImage type="submit" src="entrar" />
								</Col>
							</Row>
						</Form>
					)}
				</Formik>
			</Container>
		</Notification>
	) : (
		<ContainerGeral>
			<Row>
				<Col span={12} offset={6}>
					{notifications.errors &&
						notifications.errors.length > 0 &&
						!notifications.showInside && (
							<Error
								errors={notifications.errors}
								moveToTopWhenDisplayed={notifications.moveToTop}
							/>
						)}
				</Col>
			</Row>
			<Notification>
				<Container>
					<Header>
						<LogoBU />
					</Header>
					<Text>
						Sistema Integrado do <br /> Poupatempo
					</Text>
					<ButtonGOV onClick={handleGovBrClick}>
						<span>Entrar com</span> gov.br
					</ButtonGOV>
				</Container>
			</Notification>
			<ButtonAREA onClick={handleMinhaAreaClick} style={{ marginBottom: 15 }}>
				<span>Entrar com</span> Minha Área
			</ButtonAREA>
		</ContainerGeral>
	);
};

export default Login;
